import * as os from 'os';
import * as url from 'url';
import * as http from 'http';
import * as https from 'https';
import FormData from 'form-data';

/** The DOM EventTarget subclass used by XMLHttpRequest. */
class XMLHttpRequestEventTarget {
    onloadstart = null;
    onprogress = null;
    onabort = null;
    onerror = null;
    onload = null;
    ontimeout = null;
    onloadend = null;
    _listeners: { [eventName: string]: ((e: ProgressEvent) => void)[] } = {};

    addEventListener(eventType: string, listener: (e: ProgressEvent) => void) {
        eventType = eventType.toLowerCase();
        this._listeners[eventType] ||= [];
        this._listeners[eventType].push(listener);
    }
    removeEventListener(eventType: string, listener: (e: ProgressEvent) => void) {
        let index;
        eventType = eventType.toLowerCase();
        if (this._listeners[eventType]) {
            index = this._listeners[eventType].indexOf(listener);
            if (index !== -1) {
                this._listeners[eventType].splice(index, 1);
            }
        }
    }
    dispatchEvent(event: ProgressEvent) {
        event.currentTarget = this;
        event.target = this;
        const eventType = event.type;
        const listeners = this._listeners[eventType] || [];
        for (const listener of listeners) {
            listener?.call(this, event);
        }
        (this[`on${eventType}` as keyof typeof this] as (e: ProgressEvent) => void)?.call(this, event);
    }
}
class XMLHttpRequestUpload extends XMLHttpRequestEventTarget {
    _contentType: string | null = null;
    _body: Buffer | null = null;
    constructor(private readonly _request: XMLHttpRequest) {
        super();
    }
    _reset() {
        this._contentType = null;
        this._body = null;
    }
    _setData(data?: string | Buffer | ArrayBufferView | FormData | null) {
        if (data === undefined || data === null) {
            return;
        }
        if (typeof data === 'string') {
            if (data.length !== 0) {
                this._contentType = 'text/plain;charset=UTF-8';
            }
            this._body = Buffer.from(data, 'utf8');
        } else if (Buffer.isBuffer(data)) {
            this._body = data;
        } else if (data instanceof ArrayBuffer) {
            const body = Buffer.alloc(data.byteLength);
            const view = new Uint8Array(data);
            for (let i = 0; i < data.byteLength; i++) {
                body[i] = view[i];
            }
            this._body = body;
        } else if ('buffer' in data && data.buffer instanceof ArrayBuffer) {
            const body = Buffer.alloc(data.byteLength);
            const offset = data.byteOffset;
            const view = new Uint8Array(data.buffer);
            for (let i = 0; i < data.byteLength; i++) {
                body[i] = view[i + offset];
            }
            this._body = body;
        } else if (typeof data === 'object' && data instanceof FormData) { // The FormData might not be the same as FormData from globalThis
            this._body = null;
        } else {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            throw new Error(`Unsupported send() data ${data}`);
        }
    }
    _finalizeHeaders(headers: { [key: string]: string | undefined }, loweredHeaders: { [key: string]: string | undefined }) {
        if (this._contentType && !('content-type' in loweredHeaders)) {
            headers['Content-Type'] = this._contentType;
        }
        if (this._body) {
            headers['Content-Length'] = this._body.length.toString();
        }
    }
    _startUpload(request: http.ClientRequest) {
        if (this._body) {
            request.write(this._body);
        }
        request.end();
    }
}
class ProgressEvent {
    /** the request that caused this event */
    target: XMLHttpRequestEventTarget | null = null;
    currentTarget: XMLHttpRequestEventTarget | null = null;
    /** true if the Content-Length response header is available and the value of the event's total property is meaningful */
    lengthComputable = false;
    /** number of bytes that have already been downloaded or uploaded */
    loaded = 0;
    /** number of bytes that will be downloaded or uploaded by the request that fired the event*/
    total = 0;
    /** for compatibility with DOM events */
    bubbles = false;
    /** for compatibility with DOM events */
    cancelable = false;

    /** @param type type the event type, e.g. 'readystatechange'; must be lowercased */
    constructor(public readonly type: string) {
    }
}
/** Thrown if parsing URLs errors out. */
class SyntaxError extends Error {}
/** Thrown if there is a problem with the URL passed to the XHR. */
class NetworkError extends Error {}
/** Thrown if the XHR security policy is violated. */
class SecurityError extends Error {}
/** Usually thrown if the XHR is in the wrong readyState for an operation. */
class InvalidStateError extends Error {}
export class XMLHttpRequest extends XMLHttpRequestEventTarget {
    static SyntaxError = SyntaxError;
    static ProgressEvent = ProgressEvent;
    static SecurityError = SecurityError;
    static XMLHttpRequest = XMLHttpRequest;
    static InvalidStateError = InvalidStateError;
    static XMLHttpRequestUpload = XMLHttpRequestUpload;

    UNSENT = 0;
    OPENED = 1;
    HEADERS_RECEIVED = 2;
    LOADING = 3;
    DONE = 4;
    static UNSENT = 0;
    static OPENED = 1;
    static HEADERS_RECEIVED = 2;
    static LOADING = 3;
    static DONE = 4;

    nodejsHttpAgent = http.globalAgent;
    nodejsHttpsAgent = https.globalAgent;
    nodejsBaseUrl: string | null = null;
    private _restrictedMethods = { CONNECT: true, TRACE: true, TRACK: true };
    private _restrictedHeaders = {
        'accept-charset': true,
        'accept-encoding': true,
        'access-control-request-headers': true,
        'access-control-request-method': true,
        connection: true,
        'content-length': true,
        cookie: true,
        cookie2: true,
        date: true,
        dnt: true,
        expect: true,
        host: true,
        'keep-alive': true,
        origin: true,
        referer: true,
        te: true,
        trailer: true,
        'transfer-encoding': true,
        upgrade: true,
        'user-agent': true,
        via: true,
    };
    private _privateHeaders = { 'set-cookie': false, 'set-cookie2': false };
    private _userAgent = `Mozilla/5.0 (${os.type()} ${os.arch()}) node.js/${process.versions.node} v8/${process.versions.v8}`;

    onreadystatechange = null;
    readyState = XMLHttpRequest.UNSENT;
    response: string | Buffer | ArrayBuffer | null = null;
    responseText: string | null = '';
    responseType: string | null = '';
    status: number | undefined = 0;
    statusText: string | undefined = '';
    timeout = 0;
    upload = new XMLHttpRequestUpload(this);
    _method: string | null = null;
    _url: url.UrlWithStringQuery | null = null;
    _sync = false;
    _headers: { [header: string]: string | undefined } | null = null;
    _loweredHeaders: { [header: string]: string | undefined } | null = null;
    _mimeOverride: string | null = null;
    _request: http.ClientRequest | null = null;
    _response: http.IncomingMessage | null = null;
    _responseParts: Uint8Array[] | null = null;
    _responseHeaders: { [header: string]: string | undefined } | null = null;
    _aborting = null;
    _error = null;
    _loadedBytes = 0;
    _totalBytes = 0;
    _lengthComputable = false;
    _anonymous;

    constructor(options?: { anon?: boolean }) {
        super();
        this._anonymous = options?.anon;
    }
    static nodejsSet(options: { httpAgent?: http.Agent; httpsAgent?: https.Agent; baseUrl?: string | null }) {
        XMLHttpRequest.prototype.nodejsSet(options);
    }

    nodejsSet(options: { httpAgent?: http.Agent; httpsAgent?: https.Agent; baseUrl?: string | null }) {
        if ('httpAgent' in options) {
            this.nodejsHttpAgent = options.httpAgent!;
        }
        if ('httpsAgent' in options) {
            this.nodejsHttpsAgent = options.httpsAgent!;
        }
        if ('baseUrl' in options) {
            const baseUrl = options.baseUrl;
            if (baseUrl !== null) {
                const parsedUrl = url.parse(baseUrl!, false, true);
                if (!parsedUrl.protocol) {
                    throw new SyntaxError('baseUrl must be an absolute URL');
                }
            }
            this.nodejsBaseUrl = baseUrl!;
        }
    }
    open(method: string, _url: string, async?: boolean) {
        method = method.toUpperCase();
        if (method in this._restrictedMethods) {
            throw new SecurityError(`HTTP method ${method} is not allowed in XHR`);
        }
        const xhrUrl = this._parseUrl(_url);
        if (async === undefined) {
            async = true;
        }
        this._method = method;
        this._url = xhrUrl;
        this._sync = !async;
        this._headers = {};
        this._loweredHeaders = {};
        this._mimeOverride = null;
        this._setReadyState(XMLHttpRequest.OPENED);
        this._request = null;
        this._response = null;
        this.status = 0;
        this.statusText = '';
        this._responseParts = [];
        this._responseHeaders = null;
        this._loadedBytes = 0;
        this._totalBytes = 0;
        this._lengthComputable = false;
    }
    public setRequestHeader(name: string, value: string) {
        if (this.readyState !== XMLHttpRequest.OPENED) {
            throw new InvalidStateError('XHR readyState must be OPENED');
        }
        const loweredName = name.toLowerCase() as keyof typeof this._restrictedHeaders;
        if (this._restrictedHeaders[loweredName] || loweredName.startsWith('sec-') || loweredName.startsWith('proxy-')) {
            return;
        }
        value = value.toString();
        if (loweredName in this._loweredHeaders!) {
            name = this._loweredHeaders![loweredName]!;
            this._headers![name] = `${this._headers![name]}, ${value}`;
        } else {
            this._loweredHeaders![loweredName] = name;
            this._headers![name] = value;
        }
    }
    public send(data: string | FormData | ArrayBufferView | null | undefined) {
        if (this.readyState !== XMLHttpRequest.OPENED) {
            throw new InvalidStateError('XHR readyState must be OPENED');
        }
        if (this._request) {
            throw new InvalidStateError('send() already called');
        }
        switch (this._url!.protocol) {
            case 'file:':
                this._sendFile();
                break;
            case 'http:':
            case 'https:':
                this._sendHttp(data);
                break;
            default:
                throw new NetworkError(`XHR request to ${this._url!.href} failed: unsupported protocol '${this._url!.protocol}'. Please use supported protocols like HTTP, HTTPS, or file.`);
        }
    }
    public abort() {
        if (!this._request) {
            return;
        }
        this._request.abort();
        this._setError();
        this._dispatchProgress('abort');
        this._dispatchProgress('loadend');
    }
    public getResponseHeader(name: string) {
        const loweredName = name.toLowerCase();
        return this._responseHeaders?.[loweredName] || null;
    }
    public getAllResponseHeaders() {
        if (!this._responseHeaders) {
            return '';
        }
        return Object.entries(this._responseHeaders).map(([name, value]) => `${name}: ${value}`).join('\r\n');
    }
    public overrideMimeType(newMimeType: string) {
        if ([XMLHttpRequest.LOADING, XMLHttpRequest.DONE].includes(this.readyState)) {
            throw new InvalidStateError('overrideMimeType() not allowed in LOADING or DONE');
        }
        this._mimeOverride = newMimeType.toLowerCase();
    }
    private _setReadyState(newReadyState: number) {
        this.readyState = newReadyState;
        const event = new ProgressEvent('readystatechange');
        this.dispatchEvent(event);
    }
    private _sendFile() {
        if ((this._url as any).method !== 'GET') {
            throw new NetworkError('The file protocol only supports GET');
        }
        throw new Error('Protocol file: not implemented');
    }
    private _sendHttp(data?: string | FormData | ArrayBufferView | null) {
        if (this._sync) {
            throw new Error('Synchronous XHR processing not implemented');
        }
        if ((data != null) && (this._method === 'GET' || this._method === 'HEAD')) {
            data = null;
        } else {
            data ||= '';
        }
        this.upload._setData(data);
        this._finalizeHeaders(data);
        this._sendHxxpRequest(data);
    }
    private _sendHxxpRequest(data?: string | FormData | ArrayBufferView | null) {
        const agent = this._url!.protocol === 'http:' ? this.nodejsHttpAgent : this.nodejsHttpsAgent;
        const hxxp = this._url!.protocol === 'http:' ? http : https;
        const request = hxxp.request({
            hostname: this._url!.hostname,
            port: this._url!.port,
            path: this._url!.path,
            auth: this._url!.auth,
            method: this._method!,
            headers: this._headers!,
            agent,
        });

        if (typeof data === 'object' && data instanceof FormData) {
            data.pipe(request);
        }

        this._request = request;
        if (this.timeout) {
            request.setTimeout(this.timeout, () => this._onHttpTimeout(request));
        }
        request.on('response', response => this._onHttpResponse(request, response));
        request.on('error', () => this._onHttpRequestError(request));
        this.upload._startUpload(request);
        if (this._request === request) {
            this._dispatchProgress('loadstart');
        }
    }
    private _finalizeHeaders(data?: string | FormData | ArrayBufferView | null) {
        if (typeof data === 'object' && data instanceof FormData) {
            Object.assign(this._headers!, data.getHeaders());
        }
        this._headers!.Connection = 'keep-alive';
        this._headers!.Host = this._url!.host!;
        if (this._anonymous) {
            this._headers!.Referer = 'about:blank';
        }
        this._headers!['User-Agent'] = this._userAgent;
        this.upload._finalizeHeaders(this._headers!, this._loweredHeaders!);
    }
    private _onHttpResponse(request: http.ClientRequest, response: http.IncomingMessage) {
        if (this._request !== request) {
            return;
        }
        if ([301, 302, 303, 307, 308].includes(response.statusCode!)) {
            this._url = this._parseUrl(response.headers.location!);
            this._method = 'GET';
            if ('content-type' in this._loweredHeaders!) {
                delete this._headers![this._loweredHeaders['content-type']!];
                delete this._loweredHeaders['content-type'];
            }
            if ('Content-Type' in this._headers!) {
                delete this._headers['Content-Type'];
            }
            delete this._headers!['Content-Length'];
            this.upload._reset();
            this._finalizeHeaders();
            this._sendHxxpRequest();
            return;
        }
        this._response = response;
        this._response.on('data', data => this._onHttpResponseData(response, data));
        this._response.on('end', () => this._onHttpResponseEnd(response));
        this._response.on('close', () => this._onHttpResponseClose(response));
        this.status = this._response.statusCode;
        this.statusText = http.STATUS_CODES[this.status!];
        this._parseResponseHeaders(response);
        const lengthString = this._responseHeaders!['content-length'];
        if (lengthString) {
            this._totalBytes = parseInt(lengthString, 10);
            this._lengthComputable = true;
        } else {
            this._lengthComputable = false;
        }
        this._setReadyState(XMLHttpRequest.HEADERS_RECEIVED);
    }
    private _onHttpResponseData(response: http.IncomingMessage, data: Buffer) {
        if (this._response !== response) {
            return undefined;
        }
        this._responseParts!.push(data);
        this._loadedBytes += data.length;
        if (this.readyState !== XMLHttpRequest.LOADING) {
            this._setReadyState(XMLHttpRequest.LOADING);
        }
        return this._dispatchProgress('progress');
    }
    private _onHttpResponseEnd(response: http.IncomingMessage) {
        if (this._response !== response) {
            return undefined;
        }
        this._parseResponse();
        this._request = null;
        this._response = null;
        this._setReadyState(XMLHttpRequest.DONE);
        this._dispatchProgress('load');
        return this._dispatchProgress('loadend');
    }
    private _onHttpResponseClose(response: http.IncomingMessage) {
        if (this._response !== response) {
            return undefined;
        }
        const request = this._request;
        this._setError();
        request!.abort();
        this._setReadyState(XMLHttpRequest.DONE);
        this._dispatchProgress('error');
        return this._dispatchProgress('loadend');
    }
    private _onHttpTimeout(request: http.ClientRequest) {
        if (this._request !== request) {
            return undefined;
        }
        this._setError();
        request.abort();
        this._setReadyState(XMLHttpRequest.DONE);
        this._dispatchProgress('timeout');
        return this._dispatchProgress('loadend');
    }
    private _onHttpRequestError(request: http.ClientRequest) {
        if (this._request !== request) {
            return undefined;
        }
        this._setError();
        request.abort();
        this._setReadyState(XMLHttpRequest.DONE);
        this._dispatchProgress('error');
        return this._dispatchProgress('loadend');
    }
    private _dispatchProgress(eventType: string) {
        const event = new ProgressEvent(eventType);
        event.lengthComputable = this._lengthComputable;
        event.loaded = this._loadedBytes;
        event.total = this._totalBytes;
        this.dispatchEvent(event);
    }
    private _setError() {
        this._request = null;
        this._response = null;
        this._responseHeaders = null;
        this._responseParts = null;
    }
    private _parseUrl(urlString: string) {
        let index; let password; let user;
        const absoluteUrlString = this.nodejsBaseUrl === null ? urlString : url.resolve(this.nodejsBaseUrl, urlString);
        const xhrUrl = url.parse(absoluteUrlString, false, true);
        xhrUrl.hash = null!;
        if (xhrUrl.auth && ((user !== undefined && user !== null) || (password !== undefined && password !== null))) {
            index = xhrUrl.auth.indexOf(':');
            if (index === -1) {
                if (!user) {
                    user = xhrUrl.auth;
                }
            } else {
                if (!user) {
                    // @ts-expect-error We just defined this as a URL, which doesn't have this function 😳
                    user = xhrUrl.substring(0, index);
                }
                if (!password) {
                    // @ts-expect-error We just defined this as a URL, which doesn't have this function 😳
                    password = xhrUrl.substring(index + 1);
                }
            }
        }
        if (user || password) {
            xhrUrl.auth = `${user}:${password}`;
        }
        return xhrUrl;
    }
    private _parseResponseHeaders(response: http.IncomingMessage) {
        this._responseHeaders = {};
        Object.entries(response.headers).forEach(([name, value]) => {
            const loweredName = name.toLowerCase();
            if (this._privateHeaders[loweredName as keyof typeof this._privateHeaders]) {
                return;
            }
            if (this._mimeOverride !== null && loweredName === 'content-type') {
                value = this._mimeOverride;
            }
            this._responseHeaders![loweredName] = value as string | undefined;
        });
        if (this._mimeOverride !== null && !('content-type' in this._responseHeaders)) {
            this._responseHeaders['content-type'] = this._mimeOverride;
        }
    }
    private _parseResponse() {
        let buffer = Buffer.concat(this._responseParts!);
        // temporary workaround for transport encoding parsing
        if (this._responseHeaders!['content-encoding'] === 'gzip') {
            buffer = require('zlib').gunzipSync(buffer);
        }
        this._responseParts = null;
        switch (this.responseType) {
            case 'text':
                this._parseTextResponse(buffer);
                break;
            case 'json':
                this.responseText = null;
                try {
                    this.response = JSON.parse(buffer.toString('utf-8'));
                } catch {
                    this.response = null;
                }
                break;
            case 'buffer':
                this.responseText = null;
                this.response = buffer;
                break;
            case 'arraybuffer': {
                this.responseText = null;
                const arrayBuffer = new ArrayBuffer(buffer.length);
                const view = new Uint8Array(arrayBuffer);
                for (let i = 0; i < buffer.length; i++) {
                    view[i] = buffer[i];
                }
                this.response = arrayBuffer;
                break;
            }
            default:
                this._parseTextResponse(buffer);
        }
    }
    private _parseTextResponse(buffer: Buffer) {
        try {
            this.responseText = buffer.toString(this._parseResponseEncoding() as BufferEncoding);
        } catch {
            this.responseText = buffer.toString('binary');
        }
        this.response = this.responseText;
    }
    private _parseResponseEncoding() {
        const contentType = this._responseHeaders!['content-type'];
        const match = /;\s*charset=(.*)$/.exec(contentType!);
        if (contentType && match) {
            return match[1];
        }
        return 'utf-8';
    }
}
